import React, { useState, useEffect } from 'react';
import { Typography } from 'interfaces/typography';
import { getApiKeyInfo } from '../Authentication/utils';

// Available backend tables with their primary columns
const BACKEND_TABLES = {
  sessions: ['session_id', 'team_id', 'started_at', 'conversation_type', 'session_type', 'customer_address', 'agent_address'],
  session_metrics: ['session_id', 'user_id', 'user_name', 'call_duration', 'is_connected', 'stage', 'prospect_name', 'user_disposition', 'user_sentiment', 'user_purpose', 'platform', 'is_finalized', 'audio_duration', 'prospect_transcript_duration', 'num_words_prospect', 'num_words_rep', 'language', 'is_user_hangup', 'is_autodialer_created', 'target_duration', 'live_duration', 'gatekeeper_duration', 'voicemail_duration'],
  prospect_info: ['session_id', 'user_id', 'prospect_name', 'prospect_title', 'prospect_phone_value', 'company_name', 'company_industry', 'company_size_min', 'company_size_max', 'cadence', 'cadence_step', 'prospect_list', 'market', 'lead_type', 'platform', 'prospect_email', 'prospect_linkedin'],
  call_metrics: ['session_id', 'team_id', 'started_at', 'call_type', 'is_trellus_telephony', 'is_inbound', 'is_autodial', 'call_duration', 'customer_duration', 'live_duration', 'customer_joined_at', 'customer_live_at', 'has_customer_voicemail', 'has_agent_voicemail', 'customer_ended'],
  custom_metrics: ['session_id', 'metric_0', 'metric_1', 'metric_2', 'metric_3', 'metric_4', 'metric_5', 'metric_6', 'metric_7', 'metric_8', 'metric_9', 'metric_10', 'metric_11', 'metric_12', 'metric_13', 'metric_14', 'metric_15', 'metric_16', 'metric_17', 'metric_18', 'metric_19', 'text_0', 'text_1', 'text_2', 'text_3', 'text_4'],
  custom_details: ['session_id', 'metric_to_raw_json_str', 'metric_to_time_json_str', 'metric_to_explanation_json_str'],
  session_details_v2: ['session_id', 'team_id', 'started_at', 'cadence', 'cadence_step', 'prospect_list', 'company_persona', 'market', 'lead_type'],
  review_statuses: ['session_id', 'opening_user_id', 'is_open', 'opened_at'],
  comments: ['comment_id', 'session_id', 'user_id', 'comment', 'created_at', 'start', 'end'],
  persons_v2: ['team_id', 'platform_person_id', 'platform', 'name', 'title', 'occupation', 'seniority', 'location', 'person_linkedin'],
  companies: ['team_id', 'platform_company_id', 'platform', 'name', 'domain', 'company_url', 'company_linkedin', 'company_size', 'industry_str'],
  callback_request_details: ['session_id', 'user_id', 'team_id', 'scheduled_start', 'callback_status', 'callback_timing', 'callback_reasoning'],
  messaging_data: ['session_id', 'team_id', 'customer_bodies_str', 'customer_times_str', 'agent_bodies_str', 'agent_times_str', 'max_customer_at'],
  session_states: ['session_id', 'team_id', 'started_at', 'state_str'],
  session_agents: ['session_id', 'team_id', 'agent_ids_str', 'agent_types_str', 'joined_at_str', 'left_at_str', 'first_user_id']
};

const FILTER_OPERATORS = {
  EQ: 'Equal to',
  NEQ: 'Not equal to',
  GT: 'Greater than',
  GE: 'Greater than or equal to',
  LT: 'Less than',
  LE: 'Less than or equal to',
  IN: 'In array/list',
  NIN: 'Not in array/list',
  LIKE: 'String contains (partial match)'
};

interface APIParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  options?: string[] | { [key: string]: string[] };
  examples?: string[];
}

interface ResponseField {
  name: string;
  type: string;
  description: string;
  example?: any;
}

interface APIEndpoint {
  id: string;
  name: string;
  method: string;
  endpoint: string;
  description: string;
  parameters: APIParameter[];
  response: {
    type: string;
    description: string;
    fields?: ResponseField[];
    example?: any;
  };
  example?: string;
}

export function APIDocumentation() {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [servicesHostname, setServicesHostname] = useState<string>('');
  const [activeEndpoint, setActiveEndpoint] = useState<string>('metric-details-v6');
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    'parameters-metric-details-v6': true // Expand parameters for default endpoint
  });
  const [expandedTables, setExpandedTables] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    const loadApiKey = async () => {
      const authInfo = await getApiKeyInfo();
      if (authInfo) {
        setApiKey(authInfo.apiKey);
        setServicesHostname(authInfo.services);
      }
    };
    loadApiKey();
  }, []);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleEndpointChange = (endpointId: string) => {
    setActiveEndpoint(endpointId);
    // Expand parameters section by default for the new endpoint
    setExpandedSections(prev => ({
      ...prev,
      [`parameters-${endpointId}`]: true
    }));
  };

  const toggleTable = (tableId: string) => {
    setExpandedTables(prev => ({ ...prev, [tableId]: !prev[tableId] }));
  };

  const apiEndpoints: APIEndpoint[] = [
    {
      id: 'metric-details-v6',
      name: 'Get Metric Details v6',
      method: 'GET',
      endpoint: 'metric-details-v6',
      description: 'Retrieve detailed metrics data with flexible filtering and aggregation capabilities. This endpoint allows you to query across all session tables with complex aggregations and grouping.',
      parameters: [
        {
          name: 'api_key',
          type: 'string',
          required: true,
          description: 'Your API key for authentication',
          examples: ['YOUR_API_KEY']
        },
        {
          name: 'start',
          type: 'number',
          required: true,
          description: 'Start timestamp in microseconds (Date.getTime() * 1000)',
          examples: ['1640995200000000']
        },
        {
          name: 'end',
          type: 'number',
          required: true,
          description: 'End timestamp in microseconds (Date.getTime() * 1000)',
          examples: ['1641081600000000']
        },
        {
          name: 'selects',
          type: 'AggColumn[]',
          required: true,
          description: 'Array of columns to select and aggregate. Can be specific table columns or filtered data.',
          options: {
            'TABLE columns': [
              'sessions.session_id', 'sessions.team_id', 'sessions.started_at',
              'session_metrics.user_id', 'session_metrics.call_duration', 'session_metrics.is_connected',
              'session_metrics.user_name', 'session_metrics.prospect_name', 'session_metrics.stage',
              'prospect_info.company_name', 'prospect_info.prospect_title', 'prospect_info.cadence',
              'call_metrics.live_duration', 'call_metrics.is_inbound', 'call_metrics.customer_duration',
              'custom_metrics.metric_0', 'custom_metrics.metric_1', 'custom_metrics.text_0'
            ],
            'Filter operators': ['EQ', 'NEQ', 'GT', 'GE', 'LT', 'LE', 'IN', 'NIN', 'LIKE']
          },
          examples: [
            '[{"column":{"table":"sessions","column_name":"session_id"},"column_type":"TABLE"}]'
          ]
        },
        {
          name: 'cnf',
          type: 'Filter[][]',
          required: true,
          description: 'Filter conditions as a 2D array (Filter[][]). Outer arrays = AND logic (all must be true), inner arrays = OR logic (any can be true). Each filter targets specific table columns with operators like EQ, GT, etc.',
          examples: [
            '[[{"table":"session_metrics","column_name":"is_connected","operator":"EQ","value_safe":true}]]'
          ]
        },
        {
          name: 'group_by',
          type: 'AggGroupBy | null',
          required: false,
          description: 'Optional grouping configuration for time-based or categorical grouping',
          examples: [
            '{"column":{"table":"sessions","column_name":"started_at"},"step_size_safe":86400000000}'
          ]
        },
        {
          name: 'team_id',
          type: 'string',
          required: false,
          description: 'Team ID to filter results (auto-added if user has selected team)'
        }
      ],
      response: {
        type: '(string | number | null)[][]',
        description: 'Array of arrays containing metric data rows. Each row corresponds to the selected columns in order.',
        fields: [
          {
            name: 'Rows',
            type: 'Array<Array<string | number | null>>',
            description: 'Each inner array represents a data row with values corresponding to your selected columns',
            example: [['session_abc123', 'john_doe', 'BOOKED'], ['session_def456', 'jane_smith', 'CONVERSATION']]
          }
        ],
        example: [
          ['session_abc123', 'john_doe', 'BOOKED', 1640995200],
          ['session_def456', 'jane_smith', 'CONVERSATION', 1641000000],
          ['session_ghi789', 'bob_wilson', 'PITCHED', 1641005000]
        ]
      },
      example: `GET ${servicesHostname}/metric-details-v6?api_key=YOUR_API_KEY&start=1640995200000000&end=1641081600000000&selects=[{"column":{"table":"sessions","column_name":"session_id"},"column_type":"TABLE"}]&cnf=[]&group_by=null`
    },
    {
      id: 'session-list-v5',
      name: 'Get Session List v5',
      method: 'GET',
      endpoint: 'session-list-v5',
      description: 'Retrieve a list of sessions with flexible column selection and filtering. Perfect for building custom session tables and reports.',
      parameters: [
        {
          name: 'api_key',
          type: 'string',
          required: true,
          description: 'Your API key for authentication'
        },
        {
          name: 'cnf',
          type: 'Filter[][]',
          required: true,
          description: 'Filter conditions as a 2D array (Filter[][]). Use to narrow down which sessions to include in results.',
          examples: [
            '[[{"table":"session_metrics","column_name":"user_id","operator":"EQ","value_safe":"user_123"}]]'
          ]
        },
        {
          name: 'select',
          type: 'SelectColumn[]',
          required: true,
          description: 'Array of columns to select from sessions',
          options: {
            'Available tables': [
              'sessions', 'session_metrics', 'prospect_info', 'call_metrics',
              'custom_metrics', 'custom_details', 'session_details_v2',
              'review_statuses', 'comments', 'persons_v2', 'companies'
            ],
            'Common columns': [
              'session_id', 'team_id', 'started_at', 'user_id', 'user_name',
              'prospect_name', 'company_name', 'stage', 'call_duration',
              'is_connected', 'prospect_title', 'cadence'
            ]
          },
          examples: [
            '[{"table":"sessions","column_name":"session_id"}]'
          ]
        },
        {
          name: 'start',
          type: 'number',
          required: false,
          description: 'Start timestamp in microseconds for date filtering'
        },
        {
          name: 'end',
          type: 'number',
          required: false,
          description: 'End timestamp in microseconds for date filtering'
        },
        {
          name: 'team_id',
          type: 'string',
          required: false,
          description: 'Team ID to filter results (auto-added if user has selected team)'
        }
      ],
      response: {
        type: 'SessionListResponse[]',
        description: 'Array of session data objects with typed result mapping for selected columns',
        fields: [
          {
            name: 'sessions',
            type: 'TypedResultMap[]',
            description: 'Array of session objects with column data mapped by TypedResultMap',
            example: [{
              'session_id': 'session_abc123',
              'user_name': 'john_doe',
              'stage': 'BOOKED',
              'started_at': '2023-01-01T10:00:00Z'
            }]
          }
        ],
        example: [
          {
            'session_id': 'session_abc123',
            'user_name': 'john_doe',
            'prospect_name': 'Jane Smith',
            'stage': 'BOOKED',
            'call_duration': 300
          },
          {
            'session_id': 'session_def456',
            'user_name': 'alice_cooper',
            'prospect_name': 'Bob Johnson',
            'stage': 'CONVERSATION',
            'call_duration': 180
          }
        ]
      },
      example: `GET ${servicesHostname}/session-list-v5?api_key=YOUR_API_KEY&cnf=[]&select=[{"table":"sessions","column_name":"session_id"}]`
    },
    {
      id: 'get-visible-accounts',
      name: 'Get Visible Accounts',
      method: 'GET',
      endpoint: 'get-visible-accounts',
      description: 'Retrieve all accounts (users and teams) visible to the current user. Returns user information and team details that the current user has access to.',
      parameters: [
        {
          name: 'api_key',
          type: 'string',
          required: true,
          description: 'Your API key for authentication'
        },
        {
          name: 'team_id',
          type: 'string',
          required: false,
          description: 'Team ID to filter results (auto-added if user has selected team)'
        }
      ],
      response: {
        type: 'VisibleAccountsResult',
        description: 'Account information including users and teams visible to the current user',
        fields: [
          {
            name: 'users',
            type: 'UserDataResult[]',
            description: 'List of user accounts with their permissions and settings',
            example: [{
              user_id: 'user_123',
              user_name: 'john_doe',
              team_id: 'team_abc123',
              created_at: '2023-01-01T00:00:00Z',
              team_is_active: true,
              can_dial: true,
              autodialer_disabled: false
            }]
          },
          {
            name: 'teams',
            type: 'TeamResult[]',
            description: 'List of team accounts and their configuration settings',
            example: [{
              team_id: 'team_abc123',
              team: 'Sales Team Alpha',
              subscription_type: 'TEAM',
              has_salesfloor_access: true,
              has_practice_bot_access: true,
              use_new_dialer: true,
              autodialer_disabled: false,
              dialing_seats: 10
            }]
          }
        ],
        example: {
          users: [
            {
              user_id: 'user_123',
              user_name: 'john_doe',
              team_id: 'team_abc123',
              created_at: '2023-01-01T00:00:00Z',
              team_is_active: true,
              can_dial: true,
              autodialer_disabled: false,
              default_prospect_recording: 'ALL'
            }
          ],
          teams: [
            {
              team_id: 'team_abc123',
              team: 'Sales Team Alpha',
              subscription_type: 'TEAM',
              has_salesfloor_access: true,
              has_practice_bot_access: true,
              exclusive_domain: null,
              use_new_dialer: true,
              default_prospect_recording: 'ALL',
              autodialer_disabled: false,
              dialing_seats: 10
            }
          ]
        }
      },
      example: `GET ${servicesHostname}/get-visible-accounts?api_key=YOUR_API_KEY`
    },
    {
      id: 'get-session-data-v4',
      name: 'Get Session Data v4',
      method: 'GET',
      endpoint: 'get-session-data-v4',
      description: 'Retrieve complete detailed data for a specific session including full transcript, all metrics, agents, custom scores, and metadata.',
      parameters: [
        {
          name: 'api_key',
          type: 'string',
          required: true,
          description: 'Your API key for authentication'
        },
        {
          name: 'session_id',
          type: 'string',
          required: true,
          description: 'The unique identifier of the session',
          examples: ['session_abc123']
        }
      ],
      response: {
        type: 'SessionDataResult',
        description: 'Complete session data including transcript, agents, metrics, custom scores, and all associated metadata',
        fields: [
          {
            name: 'session',
            type: 'SessionV2',
            description: 'Core session information',
            example: {
              session_id: 'session_abc123',
              team_id: 'team_123',
              started_at: '2023-01-01T10:00:00Z',
              conversation_type: 'PHONE',
              customer_address: '+1234567890'
            }
          },
          {
            name: 'transcript',
            type: 'TranscriptSegment[]',
            description: 'Complete conversation transcript with speaker identification and timing',
            example: [{
              speaker: 'AGENT',
              text: 'Hi, this is John calling from Acme Corp...',
              start_time: 0,
              end_time: 3500
            }]
          },
          {
            name: 'session_metric',
            type: 'SessionMetric',
            description: 'All computed metrics for the session',
            example: {
              session_id: 'session_abc123',
              call_duration: 300,
              is_connected: true,
              stage: 'BOOKED',
              user_disposition: 'Meeting Scheduled'
            }
          },
          {
            name: 'custom_scores',
            type: 'CustomScoreResult[]',
            description: 'AI-generated custom metric scores',
            example: [{
              metric_id: 'metric_0',
              score: 8.5,
              explanation: 'Great rapport building demonstrated throughout call'
            }]
          },
          {
            name: 'agents',
            type: 'SessionAgents',
            description: 'Information about all participants in the session',
            example: {
              session_id: 'session_abc123',
              agent_ids_str: 'user_123|customer_456',
              agent_types_str: 'USER|CUSTOMER',
              first_user_id: 'user_123'
            }
          }
        ],
        example: {
          session: {
            session_id: 'session_abc123',
            team_id: 'team_123',
            started_at: '2023-01-01T10:00:00Z',
            conversation_type: 'PHONE',
            customer_address: '+1234567890',
            agent_address: '+1987654321'
          },
          transcript: [
            {
              speaker: 'AGENT',
              text: 'Hi, this is John calling from Acme Corp. How are you today?',
              start_time: 0,
              end_time: 3500
            },
            {
              speaker: 'CUSTOMER',
              text: 'I\'m doing well, thanks for calling.',
              start_time: 3500,
              end_time: 6000
            }
          ],
          session_metric: {
            session_id: 'session_abc123',
            call_duration: 300,
            is_connected: true,
            stage: 'BOOKED',
            user_disposition: 'Meeting Scheduled',
            prospect_name: 'Jane Smith',
            num_words_rep: 150,
            num_words_prospect: 75
          },
          custom_scores: [
            {
              metric_id: 'metric_0',
              score: 8.5,
              explanation: 'Excellent rapport building throughout the call'
            }
          ]
        }
      },
      example: `GET ${servicesHostname}/get-session-data-v4?api_key=YOUR_API_KEY&session_id=session_abc123`
    }
  ];

  const toggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const activeEndpointData = apiEndpoints.find(ep => ep.id === activeEndpoint);

  const renderExpandableTableColumns = () => {
    return (
      <div className="mt-3">
        <button
          onClick={() => toggleSection('all-tables')}
          className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <span>{expandedSections['all-tables'] ? '▼' : '▶'}</span>
          <span>View All Available Tables & Columns</span>
        </button>

        {expandedSections['all-tables'] && (
          <div className="mt-3 space-y-3 bg-gray-50 p-4 rounded-lg border">
            {Object.entries(BACKEND_TABLES).map(([tableName, columns]) => (
              <div key={tableName} className="border-b border-gray-200 pb-3 last:border-b-0">
                <button
                  onClick={() => toggleTable(tableName)}
                  className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
                >
                  <span className="text-xs">{expandedTables[tableName] ? '▼' : '▶'}</span>
                  <code className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded">{tableName}</code>
                  <Typography variant="smallParagraph" color="#6B7280">({columns.length} columns)</Typography>
                </button>

                {expandedTables[tableName] && (
                  <div className="ml-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {columns.map((column, i) => (
                      <code key={i} className="text-xs bg-white border px-2 py-1 rounded text-gray-600">
                        {column}
                      </code>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFilterStructure = () => {
    return (
      <div className="mt-3">
        <button
          onClick={() => toggleSection('filter-structure')}
          className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <span>{expandedSections['filter-structure'] ? '▼' : '▶'}</span>
          <span>Filter Structure</span>
        </button>

        {expandedSections['filter-structure'] && (
          <div className="mt-3 bg-gray-50 p-4 rounded-lg border">
            <Typography variant="smallParagraph" color="#374151" className="mb-3">
              Filters are arrays of arrays containing conditions that help you narrow down your data. The outer array represents AND conditions (all must be true), while inner arrays represent OR conditions (any can be true): <code>FilterColumn[][]</code>
            </Typography>

            <div className="space-y-4">
              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">FilterColumn Structure:</Typography>
                <div className="bg-gray-900 text-white p-3 rounded text-xs font-mono">
{`{
  table: "sessions" | "session_metrics" | "prospect_info" | ...,
  column_name: "session_id" | "user_name" | "stage" | ...,
  operator: "EQ" | "NEQ" | "GT" | "GE" | "LT" | "LE" | "IN" | "NIN" | "LIKE",
  value_safe: any // the value to filter by
}`}
                </div>
              </div>

              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">Filter Operators:</Typography>
                <div className="space-y-1">
                  {Object.entries(FILTER_OPERATORS).map(([op, desc]) => (
                    <div key={op} className="flex items-center space-x-2">
                      <code className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs w-12 text-center">{op}</code>
                      <Typography variant="smallParagraph" color="#6B7280">{desc}</Typography>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">Example Usage:</Typography>
                <div className="bg-gray-900 text-white p-3 rounded text-xs font-mono whitespace-pre">
{`// Filter for connected calls by user "john_doe" with stage BOOKED or CONVERSATION
[
  [
    {
      table: "session_metrics",
      column_name: "is_connected",
      operator: "EQ",
      value_safe: true
    },
    {
      table: "session_metrics",
      column_name: "user_name",
      operator: "EQ",
      value_safe: "john_doe"
    }
  ],
  [
    {
      table: "session_metrics",
      column_name: "stage",
      operator: "IN",
      value_safe: ["BOOKED", "CONVERSATION"]
    }
  ]
]`}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAggGroupByStructure = () => {
    return (
      <div className="mt-3">
        <button
          onClick={() => toggleSection('agg-group-by')}
          className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <span>{expandedSections['agg-group-by'] ? '▼' : '▶'}</span>
          <span>AggGroupBy Structure</span>
        </button>

        {expandedSections['agg-group-by'] && (
          <div className="mt-3 bg-gray-50 p-4 rounded-lg border">
            <Typography variant="smallParagraph" color="#374151" className="mb-3">
              AggGroupBy allows grouping results by time intervals or categorical values.
            </Typography>

            <div className="space-y-4">
              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">Structure:</Typography>
                <div className="bg-gray-900 text-white p-3 rounded text-xs font-mono">
{`{
  column: {
    table: "sessions" | "session_metrics" | ...,
    column_name: "started_at" | "stage" | ...
  },
  step_size_safe?: number,    // For time-based grouping (microseconds)
  step_start_safe?: number,   // Start time for time buckets
  step_modulus_safe?: number  // Modulus for custom intervals
}`}
                </div>
              </div>

              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">Time-based Grouping Examples:</Typography>
                <div className="space-y-2">
                  <div className="bg-gray-900 text-white p-2 rounded text-xs font-mono">
{`// Group by day (86400 seconds = 1 day in microseconds)
{
  column: { table: "sessions", column_name: "started_at" },
  step_size_safe: 86400000000
}`}
                  </div>
                  <div className="bg-gray-900 text-white p-2 rounded text-xs font-mono">
{`// Group by hour
{
  column: { table: "sessions", column_name: "started_at" },
  step_size_safe: 3600000000
}`}
                  </div>
                </div>
              </div>

              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">Categorical Grouping Example:</Typography>
                <div className="bg-gray-900 text-white p-2 rounded text-xs font-mono">
{`// Group by stage
{
  column: { table: "session_metrics", column_name: "stage" }
}`}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAggColumnStructure = () => {
    return (
      <div className="mt-3">
        <button
          onClick={() => toggleSection('agg-column')}
          className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <span>{expandedSections['agg-column'] ? '▼' : '▶'}</span>
          <span>AggColumn Structure</span>
        </button>

        {expandedSections['agg-column'] && (
          <div className="mt-3 bg-gray-50 p-4 rounded-lg border">
            <Typography variant="smallParagraph" color="#374151" className="mb-3">
              AggColumn can be either specific table columns or filtered data for aggregation.
            </Typography>

            <div className="space-y-4">
              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">TABLE Type (select specific columns):</Typography>
                <div className="bg-gray-900 text-white p-3 rounded text-xs font-mono">
{`{
  column: {
    table: "session_metrics",
    column_name: "user_name"
  },
  column_type: "TABLE"
}`}
                </div>
              </div>

              <div>
                <Typography variant="caption" color="#6B7280" className="mb-2">FILTER Type (aggregate filtered results):</Typography>
                <div className="bg-gray-900 text-white p-3 rounded text-xs font-mono">
{`{
  cnf: [
    [
      {
        table: "session_metrics",
        column_name: "stage",
        operator: "EQ",
        value_safe: "BOOKED"
      }
    ]
  ],
  column_type: "FILTER"
}`}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResponseStructure = (response: { type: string; description: string; fields?: ResponseField[]; example?: any }) => {
    const responseId = `response-${activeEndpoint}`;

    return (
      <div className="mb-8">
        <Typography variant="h3" color="#111827" fontWeight={600} className="mb-4">
          Response
        </Typography>
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-start space-x-3 mb-3">
            <code className="text-sm text-purple-600 bg-purple-50 px-3 py-1 rounded font-mono">
              {response.type}
            </code>
          </div>
          <Typography variant="largeParagraph" color="#374151" className="mb-4">
            {response.description}
          </Typography>

          {response.fields && (
            <div>
              <button
                onClick={() => toggleSection(responseId)}
                className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium mb-3"
              >
                <span>{expandedSections[responseId] ? '▼' : '▶'}</span>
                <span>View Response Structure</span>
              </button>

              {expandedSections[responseId] && (
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <div className="space-y-4">
                    {response.fields.map((field, index) => (
                      <div key={index} className="border-b border-gray-200 pb-3 last:border-b-0">
                        <div className="flex items-start space-x-3 mb-2">
                          <code className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded font-mono">
                            {field.name}
                          </code>
                          <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded font-mono">
                            {field.type}
                          </code>
                        </div>
                        <Typography variant="smallParagraph" color="#374151" className="mb-2">
                          {field.description}
                        </Typography>
                        {field.example && (
                          <div>
                            <Typography variant="caption" color="#6B7280" className="mb-1">Example:</Typography>
                            <div className="bg-gray-900 text-white p-2 rounded text-xs font-mono">
                              {JSON.stringify(field.example, null, 2)}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {response.example && (
            <div className="mt-4">
              <Typography variant="caption" color="#6B7280" className="mb-2">Complete Response Example:</Typography>
              <div className="bg-gray-900 text-white p-3 rounded text-xs font-mono overflow-x-auto">
                {JSON.stringify(response.example, null, 2)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderParameterOptions = (param: APIParameter) => {
    const { name, options } = param;

    if (!options) return null;

    // Special handling for complex parameter types
    if (name === 'cnf') {
      return renderFilterStructure();
    }
    if (name === 'group_by') {
      return renderAggGroupByStructure();
    }
    if (name === 'selects') {
      return (
        <div>
          {renderAggColumnStructure()}
          {renderExpandableTableColumns()}
        </div>
      );
    }
    if (name === 'select') {
      return renderExpandableTableColumns();
    }

    if (Array.isArray(options)) {
      return (
        <div className="mt-2">
          <Typography variant="caption" color="#6B7280">Options:</Typography>
          <div className="flex flex-wrap gap-1 mt-1">
            {options.slice(0, 10).map((option, i) => (
              <code key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                {option}
              </code>
            ))}
            {options.length > 10 && (
              <Typography variant="smallParagraph" color="#6B7280">...and {options.length - 10} more</Typography>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="mt-2">
        {Object.entries(options).map(([category, opts]) => (
          <div key={category} className="mb-2">
            <Typography variant="caption" color="#6B7280">{category}:</Typography>
            <div className="flex flex-wrap gap-1 mt-1">
              {opts.slice(0, 8).map((option, i) => (
                <code key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                  {option}
                </code>
              ))}
              {opts.length > 8 && (
                <Typography variant="smallParagraph" color="#6B7280">...and {opts.length - 8} more</Typography>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="h-full w-full flex bg-gray-50">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex-shrink-0 shadow-sm">
        {/* API Key Section */}
        <div className="p-6 border-b border-gray-200">
          <Typography variant="h4" color="#111827" fontWeight={600} className="mb-3">
            Your API Key
          </Typography>
          <div className="space-y-3">
            <div className="font-mono text-xs bg-gray-50 p-3 rounded border break-all">
              {showApiKey ? (
                <span className="select-all">{apiKey || 'No API key found'}</span>
              ) : (
                <span>{'•'.repeat(32)}</span>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={toggleApiKeyVisibility}
                className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
              >
                {showApiKey ? 'Hide' : 'Show'}
              </button>
              {apiKey && (
                <button
                  onClick={() => copyToClipboard(apiKey)}
                  className="px-3 py-1 text-xs font-medium text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50 transition-colors"
                >
                  Copy
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
            <Typography variant="smallParagraph" color="#1E40AF" fontWeight={600}>
              Base URL: <code className="font-mono text-xs">{servicesHostname}</code>
            </Typography>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto">
          <div className="p-4">
            <Typography variant="caption" color="#6B7280" className="mb-3">
              API Endpoints
            </Typography>
            <div className="space-y-1">
              {apiEndpoints.map((endpoint) => (
                <button
                  key={endpoint.id}
                  onClick={() => handleEndpointChange(endpoint.id)}
                  className={`w-full text-left px-3 py-3 rounded-lg transition-colors duration-200 ${
                    activeEndpoint === endpoint.id
                      ? "bg-blue-50 border border-blue-200"
                      : "hover:bg-gray-50 border border-transparent"
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      endpoint.method === 'GET' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {endpoint.method}
                    </span>
                  </div>
                  <Typography
                    variant="mediumParagraph"
                    color={activeEndpoint === endpoint.id ? "#1E40AF" : "#374151"}
                    fontWeight={activeEndpoint === endpoint.id ? 600 : 500}
                  >
                    {endpoint.name}
                  </Typography>
                  <Typography variant="smallParagraph" color="#6B7280" className="mt-1">
                    {endpoint.endpoint}
                  </Typography>
                </button>
              ))}
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-auto">
          {activeEndpointData && (
            <div className="p-8">
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center space-x-4 mb-4">
                  <span className={`px-3 py-1 text-sm font-semibold rounded ${
                    activeEndpointData.method === 'GET' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {activeEndpointData.method}
                  </span>
                  <Typography variant="h2" color="#111827" fontWeight={700}>
                    {activeEndpointData.name}
                  </Typography>
                </div>
                <Typography variant="largeParagraph" color="#6B7280" className="mb-4">
                  {activeEndpointData.description}
                </Typography>
                <div className="font-mono text-sm bg-gray-900 text-white p-4 rounded-lg">
                  <span className="text-green-400">{activeEndpointData.method}</span>{' '}
                  <span className="text-yellow-300">{servicesHostname}/{activeEndpointData.endpoint}</span>
                </div>
              </div>

              {/* Parameters */}
              <div className="mb-8">
                <button
                  onClick={() => toggleSection(`parameters-${activeEndpoint}`)}
                  className="flex items-center space-x-2 mb-4 text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors"
                >
                  <span>{expandedSections[`parameters-${activeEndpoint}`] ? '▼' : '▶'}</span>
                  <Typography variant="h3" color="#111827" fontWeight={600}>
                    Parameters
                  </Typography>
                </button>

                {expandedSections[`parameters-${activeEndpoint}`] && (
                  <div className="space-y-4">
                    {activeEndpointData.parameters.map((param, paramIndex) => (
                      <div key={paramIndex} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-start space-x-4 mb-3">
                          <code className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded font-mono">
                            {param.name}
                          </code>
                          <code className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                            {param.type}
                          </code>
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${
                            param.required
                              ? 'text-red-800 bg-red-100'
                              : 'text-gray-600 bg-gray-100'
                          }`}>
                            {param.required ? 'Required' : 'Optional'}
                          </span>
                        </div>
                        <Typography variant="smallParagraph" color="#374151" className="mb-2">
                          {param.description}
                        </Typography>

                        {renderParameterOptions(param)}

                        {param.examples && (
                          <div className="mt-3">
                            <Typography variant="caption" color="#6B7280" className="mb-2">Examples:</Typography>
                            <div className="space-y-1">
                              {param.examples.map((example, i) => (
                                <div key={i} className="bg-gray-50 p-2 rounded text-xs font-mono break-all">
                                  {example}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Response */}
              {renderResponseStructure(activeEndpointData.response)}

              {/* Example Request */}
              {activeEndpointData.example && (
                <div className="mb-8">
                  <Typography variant="h3" color="#111827" fontWeight={600} className="mb-4">
                    Example Request
                  </Typography>
                  <div className="bg-gray-900 text-white p-4 rounded-lg overflow-x-auto">
                    <code className="text-sm font-mono whitespace-pre-wrap break-all">
                      {activeEndpointData.example}
                    </code>
                  </div>
                  <button
                    onClick={() => copyToClipboard(activeEndpointData.example!)}
                    className="mt-3 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    Copy Example
                  </button>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Typography variant="mediumParagraph" color="#92400E" fontWeight={600} className="mb-2">
                    Authentication
                  </Typography>
                  <Typography variant="largeParagraph" color="#92400E">
                    All endpoints require authentication via the <code className="bg-yellow-100 px-1 rounded">api_key</code> parameter. Include your API key in every request.
                  </Typography>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Typography variant="mediumParagraph" color="#1E40AF" fontWeight={600} className="mb-2">
                    Rate Limiting
                  </Typography>
                  <Typography variant="largeParagraph" color="#1E40AF">
                    API requests may be subject to rate limiting. If you encounter rate limit errors, please reduce your request frequency.
                  </Typography>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}